import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";

import { environment } from "../../../environments/environment";

/* ============================================================
   Connection
============================================================ */

export interface SquareBusiness {
  id: string;
  name: string;
}

export interface SquareConnection {
  merchant_id: string;
  merchant_name: string;
  country: string;
  currency: string;
  merchant_status: string;
  main_location_id: string;
  environment: string;
  scopes: string[];

  connected_at: string | null;
  last_synced_at: string | null;
  token_expires_at: string | null;
}

export interface SquareStatusResponse {
  ok: boolean;
  connected: boolean;

  business?: SquareBusiness;
  square?: SquareConnection;

  detail?: string;
}

export interface SquareConnectResponse {
  ok: boolean;
  business: SquareBusiness;
  environment: string;
  authorization_url: string;
}

export interface SquareDisconnectResponse {
  ok: boolean;
  connected: boolean;
}

/* ============================================================
   Setup - Locations
============================================================ */

export interface GlogetSalonSetup {
  id: string;
  name: string;

  email?: string | null;
  phone?: string | null;

  address?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
}

export interface SquareLocationMappingInfo {
  salon_id: string;
  salon_name: string;
}

export interface SquareLocationSetup {
  id: string;

  name: string;

  business_name?: string;

  business_email?: string;

  description?: string;

  status?: string;

  timezone?: string;

  phone_number?: string;

  address?: {
    address_line_1?: string;
    address_line_2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };

  mapping: SquareLocationMappingInfo | null;
}

/* ============================================================
   Setup - Services
============================================================ */

export interface GlogetServiceSetup {
  id: string;
  name: string;
  price: string;
  duration_minutes: number;

  salon: {
    id: string;
    name: string;
  };
}

export interface SquareServiceSetup {
  item_id: string;

  variation_id: string;

  version?: number | null;

  item_name: string;

  variation_name: string;

  display_name: string;

  available_for_booking?: boolean;

  duration_minutes?: number | null;

  service_duration_ms?: number | null;

  price_money?: {
    amount?: number | null;
    currency?: string | null;
  };

  mapping: {
    service_id: string;
    service_name: string;
    salon_id: string;
    salon_name: string;
  } | null;
}

/* ============================================================
   Setup - Staff
============================================================ */

export interface GlogetStaffSetup {
  id: string;

  name: string;

  first_name?: string;
  last_name?: string;

  email?: string | null;

  phone_number?: string | null;

  job_title?: string | null;

  salon: {
    id: string;
    name: string;
  };
}

export interface SquareTeamMemberBookingProfile {
  team_member_id: string;

  display_name?: string;

  is_bookable: boolean;
}

export interface SquareStaffSetup {
  id: string;

  status?: string;

  given_name?: string;

  family_name?: string;

  email_address?: string;

  phone_number?: string;

  is_owner?: boolean;

  display_name: string;

  booking_display_name?: string;

  is_bookable: boolean;

  booking_profile: SquareTeamMemberBookingProfile | null;

  mapping: {
    staff_id: string;
    staff_name: string;

    salon_id: string;
    salon_name: string;
  } | null;
}

/* ============================================================
   Full setup response
============================================================ */

export interface SquareSetupResponse {
  ok: boolean;

  business: SquareBusiness;

  locations: {
    gloget: GlogetSalonSetup[];
    square: SquareLocationSetup[];
  };

  services: {
    gloget: GlogetServiceSetup[];
    square: SquareServiceSetup[];
  };

  staff: {
    gloget: GlogetStaffSetup[];
    square: SquareStaffSetup[];
  };
}

/* ============================================================
   Mapping responses
============================================================ */

export interface SquareLocationMapResponse {
  ok: boolean;

  mapping: {
    square_location_id: string;
    square_location_name: string;
    salon_id: string;
    salon_name: string;
  };
}

/* ============================================================
   Service
============================================================ */

@Injectable({
  providedIn: "root",
})
export class IntegrationsService {
  private readonly http = inject(HttpClient);

  private readonly apiUrl = environment.apiUrl;

  private readonly squareBaseUrl = `${this.apiUrl}/integrations/square`;

  /* ----------------------------------------------------------
     Connection
  ---------------------------------------------------------- */

  getSquareStatus(): Observable<SquareStatusResponse> {
    return this.http.get<SquareStatusResponse>(`${this.squareBaseUrl}/status/`);
  }

  connectSquare(): Observable<SquareConnectResponse> {
    return this.http.get<SquareConnectResponse>(
      `${this.squareBaseUrl}/connect/`,
    );
  }

  disconnectSquare(): Observable<SquareDisconnectResponse> {
    return this.http.post<SquareDisconnectResponse>(
      `${this.squareBaseUrl}/disconnect/`,
      {},
    );
  }

  /* ----------------------------------------------------------
     Setup
  ---------------------------------------------------------- */

  getSquareSetup(): Observable<SquareSetupResponse> {
    return this.http.get<SquareSetupResponse>(`${this.squareBaseUrl}/setup/`);
  }

  /* ----------------------------------------------------------
     Location mapping
  ---------------------------------------------------------- */

  mapSquareLocation(
    squareLocationId: string,
    salonId: string,
  ): Observable<SquareLocationMapResponse> {
    return this.http.post<SquareLocationMapResponse>(
      `${this.squareBaseUrl}/setup/location/map/`,
      {
        square_location_id: squareLocationId,

        salon_id: salonId,
      },
    );
  }

  /*
   * These next two endpoints are the backend endpoints
   * we will add immediately after the UI:
   *
   * Square location -> create Gloget Salon
   */
  createGlogetSalonFromSquare(payload: {
    square_location_id: string;
    name?: string;
    email?: string;
    phone?: string;
  }): Observable<any> {
    return this.http.post(
      `${this.squareBaseUrl}/setup/location/create-gloget/`,
      payload,
    );
  }

  /*
   * Gloget Salon -> create Square Location
   */
  createSquareLocationFromGloget(salonId: string): Observable<any> {
    return this.http.post(
      `${this.squareBaseUrl}/setup/location/create-square/`,
      {
        salon_id: salonId,
      },
    );
  }

  /* ----------------------------------------------------------
     Service mapping - already prepared for next step
  ---------------------------------------------------------- */

  mapSquareService(
    squareVariationId: string,
    serviceId: string,
  ): Observable<any> {
    return this.http.post(`${this.squareBaseUrl}/setup/service/map/`, {
      square_variation_id: squareVariationId,

      service_id: serviceId,
    });
  }

  createGlogetServiceFromSquare(payload: {
    square_variation_id: string;
    salon_id: string;
    name?: string;
    price?: string;
    duration_minutes?: number;
  }): Observable<any> {
    return this.http.post(
      `${this.squareBaseUrl}/setup/service/create-gloget/`,
      payload,
    );
  }

  createSquareServiceFromGloget(serviceId: string): Observable<any> {
    return this.http.post(
      `${this.squareBaseUrl}/setup/service/create-square/`,
      {
        service_id: serviceId,
      },
    );
  }

  /* ----------------------------------------------------------
     Staff mapping
  ---------------------------------------------------------- */

  mapSquareStaff(squareTeamMemberId: string, staffId: string): Observable<any> {
    return this.http.post(`${this.squareBaseUrl}/setup/staff/map/`, {
      square_team_member_id: squareTeamMemberId,

      staff_id: staffId,
    });
  }

  createGlogetStaffFromSquare(payload: {
    square_team_member_id: string;

    salon_id: string;

    first_name: string;
    last_name: string;

    email: string;

    phone_number?: string;

    job_title?: string;
  }): Observable<any> {
    return this.http.post(
      `${this.squareBaseUrl}/setup/staff/create-gloget/`,
      payload,
    );
  }

  createSquareStaffFromGloget(staffId: string): Observable<any> {
    return this.http.post(`${this.squareBaseUrl}/setup/staff/create-square/`, {
      staff_id: staffId,
    });
  }
}
